/** 素材/模版/色板接口所需的店铺上下文（departmentId 等 query 参数） */

export function toPlatformStoreId(shopId) {
  const id = String(shopId || '1').replace(/^s/i, '');
  return `s${id}`;
}

export function resolveShopId(storeId, user, stores = []) {
  if (storeId && storeId !== 'all') {
    const store = resolveStoreRecord(storeId, stores);
    if (store?.departmentId != null && Number(store.departmentId) > 0) {
      return String(store.departmentId);
    }
    return String(storeId).replace(/^s/i, '');
  }
  if (user?.departmentId != null && Number(user.departmentId) > 0) {
    return String(user.departmentId);
  }
  const first = user?.stores?.[0] || stores[0]?.id;
  if (first) {
    const store = resolveStoreRecord(first, stores);
    if (store?.departmentId != null && Number(store.departmentId) > 0) {
      return String(store.departmentId);
    }
    return String(first).replace(/^s/i, '');
  }
  return '1';
}

export function resolveUserId(user) {
  const envId = import.meta.env.VITE_API_USER_ID;
  if (envId) return String(envId);
  const raw = user?.id ?? user?.userId;
  if (raw == null || String(raw).trim() === '') return '1567';
  const digits = String(raw).replace(/\D/g, '');
  return digits || '1567';
}

/** departmentId = 店铺 ID，departmentName = 店铺名称 */
export function resolveDepartmentStoreKey(storeId, user, stores = []) {
  if (storeId && storeId !== 'all') return toPlatformStoreId(resolveShopId(storeId, user, stores));
  const first = user?.stores?.[0] || stores[0]?.id;
  return first ? toPlatformStoreId(resolveShopId(first, user, stores)) : 's1';
}

export function resolveStoreRecord(storeId, stores = []) {
  if (!storeId || storeId === 'all') return null;
  const normalized = toPlatformStoreId(String(storeId).replace(/^s/i, ''));
  const numeric = String(storeId).replace(/^s/i, '');
  return stores.find((item) => {
    if (!item) return false;
    if (item.id === storeId || item.id === normalized) return true;
    if (String(item.id) === String(storeId)) return true;
    if (item.departmentId != null && String(item.departmentId) === numeric) return true;
    return false;
  }) || null;
}

export function isAllPlatformStore(storeId) {
  if (!storeId || storeId === 'all') return true;
  const normalized = String(storeId).toLowerCase();
  // 全平台色板/全局 shopId（VITE_GLOBAL_SHOP_ID 默认 0）与组织树「全平台」等价
  return normalized === '0' || normalized === 's0';
}

export function resolvePlatformStorageKey(storeId, user, stores = []) {
  if (isAllPlatformStore(storeId)) return 'all';
  return toPlatformStoreId(resolveShopId(storeId, user, stores));
}

export function resolveDepartmentFromStore(storeId, stores = [], sessionDept = null) {
  if (sessionDept && (sessionDept.storeId === storeId || isAllPlatformStore(storeId) || !storeId)) {
    if (sessionDept.departmentId != null && Number(sessionDept.departmentId) > 0) {
      return {
        departmentId: Number(sessionDept.departmentId),
        departmentName: sessionDept.departmentName || '',
        storeId: sessionDept.storeId || toPlatformStoreId(sessionDept.departmentId),
      };
    }
  }

  const store = resolveStoreRecord(storeId, stores);
  if (store) {
    const deptId = store.departmentId != null && Number(store.departmentId) > 0
      ? Number(store.departmentId)
      : (Number(String(store.id || '').replace(/^s/i, '')) || null);
    const deptName = store.name || store.departmentName || '';
    if (deptId) {
      return {
        departmentId: deptId,
        departmentName: deptName,
        storeId: store.id || toPlatformStoreId(deptId),
      };
    }
  }

  const firstStore = Array.isArray(stores) && stores.length ? stores[0] : null;
  if (firstStore && (isAllPlatformStore(storeId) || !storeId)) {
    const deptId = firstStore.departmentId != null && Number(firstStore.departmentId) > 0
      ? Number(firstStore.departmentId)
      : (Number(String(firstStore.id || '').replace(/^s/i, '')) || null);
    return {
      departmentId: deptId,
      departmentName: firstStore.name || firstStore.departmentName || '',
      storeId: firstStore.id || (deptId ? toPlatformStoreId(deptId) : 's1'),
    };
  }

  const numericStr = String(storeId || '').replace(/^s/i, '');
  const numeric = Number(numericStr);
  const validNum = Number.isFinite(numeric) && numeric > 0 ? numeric : (firstStore?.departmentId || null);
  return {
    departmentId: validNum,
    departmentName: store?.name || store?.departmentName || sessionDept?.departmentName || '',
    storeId: validNum ? toPlatformStoreId(validNum) : (storeId || 's1'),
  };
}

/**
 * 切换组织树时同步 session 上的部门上下文，并可选持久化
 */
export function applySessionDepartment(session, storeId, stores = [], storage = null) {
  if (!session) return null;
  session.storeId = storeId;
  if (!storeId || storeId === 'all') {
    session.departmentId = null;
    session.departmentName = '全平台';
    if (storage) {
      storage.removeItem('cdp_department_id');
      storage.removeItem('cdp_department_name');
    }
    return { departmentId: null, departmentName: '全平台', storeId: 'all' };
  }
  const dept = resolveDepartmentFromStore(storeId, stores);
  session.departmentId = dept.departmentId;
  session.departmentName = dept.departmentName;
  if (storage) {
    storage.setItem('cdp_department_id', String(dept.departmentId || ''));
    storage.setItem('cdp_department_name', dept.departmentName || '');
  }
  return dept;
}

export function restoreSessionDepartment(session, stores = [], storage = null) {
  if (!session || !storage || session.storeId === 'all') return;
  const savedId = storage.getItem('cdp_department_id');
  const savedName = storage.getItem('cdp_department_name');
  if (savedId != null && savedId !== '' && Number(savedId) > 0) {
    session.departmentId = Number(savedId);
    session.departmentName = savedName || session.departmentName || '';
    return;
  }
  const dept = resolveDepartmentFromStore(session.storeId, stores);
  session.departmentId = dept.departmentId;
  session.departmentName = dept.departmentName;
}

export function getMaterialDepartmentContext(storeId, user, stores = [], sessionDept = null) {
  if (isAllPlatformStore(storeId) && !sessionDept) {
    return { departmentId: null, departmentName: '全平台', storeId: 'all' };
  }
  return resolveDepartmentFromStore(storeId, stores, sessionDept);
}

/** GET 列表类接口：全平台不传 departmentId */
export function getMaterialDepartmentIdParam(storeId, user, stores = [], sessionDept = null) {
  if (isAllPlatformStore(storeId) && !sessionDept) return {};
  const { departmentId } = resolveDepartmentFromStore(storeId, stores, sessionDept);
  return departmentId != null ? { departmentId: Number(departmentId) } : {};
}

/** POST 等写操作：全平台不传 departmentId / departmentName */
export function getDepartmentRequestParams(storeId, user, stores = [], sessionDept = null) {
  if (isAllPlatformStore(storeId) && !sessionDept) return {};
  const { departmentId, departmentName } = resolveDepartmentFromStore(storeId, stores, sessionDept);
  const result = {};
  if (departmentId != null) {
    result.departmentId = Number(departmentId);
  }
  if (departmentName) {
    result.departmentName = departmentName;
  }
  return result;
}

export function getActiveDepartmentContext(context = {}) {
  const {
    storeId,
    user,
    stores = [],
    departmentId,
    departmentName,
  } = context;
  const sessionDept = departmentId != null
    ? { storeId, departmentId, departmentName }
    : null;
  return getMaterialDepartmentContext(storeId, user, stores, sessionDept);
}

export function getActiveDepartmentIdParam(context = {}) {
  const { storeId } = context;
  if (isAllPlatformStore(storeId)) return {};
  return getMaterialDepartmentIdParam(
    storeId,
    context.user,
    context.stores || [],
    context.departmentId != null
      ? { storeId, departmentId: context.departmentId, departmentName: context.departmentName }
      : null,
  );
}

export function buildDepartmentOptions(apiContext = {}) {
  const { storeId, user, stores = [], departmentId, departmentName } = apiContext;
  const sessionDept = departmentId != null && Number(departmentId) > 0
    ? { storeId: storeId || toPlatformStoreId(departmentId), departmentId: Number(departmentId), departmentName: departmentName || '' }
    : null;
  return { stores, sessionDept, user };
}

/** 列表同步：全平台只发一组不带 departmentId 的请求 */
export function resolveSyncStoreIds(context = {}) {
  const { storeId } = context;
  if (isAllPlatformStore(storeId)) return ['all'];
  return [storeId];
}
